import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ActionSelector from './ActionSelector';
import FrameworkActionArgumentFields from './FrameworkActionArgumentFields';
import { BUTTON_LABEL as CONTROLLER_BUTTON_LABEL } from './controllerConstants';
import { BUTTON_LABEL as DRIVEBASE_BUTTON_LABEL } from '../Drivebase/drivebasePanelConstants';
import { translateControllerInputButton, translateDrivebaseInputButton } from './inputButtonI18n';
import { useAppSelector } from '../../../redux/ReduxHooks';
import type { FrameworkAction, FrameworkArgument } from '../../../framework/framework.d';
import { getEnumOptions } from '../../../framework/framework-enums';
import { getInputArgNameForAxisAction } from '../../../framework/framework-axis-actions';
import {
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  isTupleArg,
  readTupleMemberValue,
  sequenceTupleArgValueFromArgValues,
  sequenceTupleRowsToArgValues,
  tupleMemberSchemaDefault,
  tupleMemberValueKey,
  tupleValueFromArgValues,
} from '../../../utils/tupleArg';
import { scalarFrameworkArgIsValid, sequenceTupleRowsAreValid } from '../../../utils/sequenceTupleValidation';
import { isUnsupportedSequenceTupleLiteral } from '../pythonArgParser';
import { useTranslation } from 'react-i18next';
import {
  coerceFrameworkArgValuePreservingExpressions,
  isFrameworkArgOptionalNoneDefault,
  isFrameworkNumberArgType,
} from '../../../utils/frameworkArgTypes';
import {
  buildActionLabel,
  clearRawActionArgValue,
  copyRawActionArgValues,
  findActionDefForLabel,
  findActionItemForLabel,
  getRawActionArgValue,
  parseActionLabel,
  setRawActionArgValue,
  updateRawActionArgValue,
} from '../../CodeEditor/ConfigurationTabs/driveModeAutonomousActionUtils';
import { getLocalizedFrameworkArgLabel, getLocalizedFrameworkUnit } from '../../../utils/frameworkI18n';
import { useAllAvailableActions } from '../../../hooks/editor/useAllAvailableActions';
import { excludeGroupOnlyActions } from '../../CodeEditor/ConfigurationTabs/hardwareParser';
import type { InputBinding, InputBindingAction, SingleActionInputBinding } from '../../../utils/inputBindings';
import {
  canUseVariableInputBinding,
  dedupeInputBindingsBySignature,
  getSupportedInputBindingsForButtonEditor,
  INPUT_BINDING_KIND,
  inputBindingSignature,
  withInputBindingId,
} from '../../../utils/inputBindings';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';

const isFollowPathAction = (name: string | undefined): boolean => name === 'follow_path_action';
const FRAMEWORK_ENUM_OPTIONS = getEnumOptions();

type EditableBindingKind = SingleActionInputBinding['kind'] | typeof INPUT_BINDING_KIND.TOGGLE;

interface SingleActionBindingDraft {
  draftId: string;
  kind: SingleActionInputBinding['kind'];
  actionLabel: string;
  argValues: Record<string, string>;
}

interface ToggleBindingDraft {
  draftId: string;
  kind: typeof INPUT_BINDING_KIND.TOGGLE;
  onActionLabel: string;
  onArgValues: Record<string, string>;
  offActionLabel: string;
  offArgValues: Record<string, string>;
}

type EditableBindingDraft = SingleActionBindingDraft | ToggleBindingDraft;
type ArgHydrationMode = 'semantic' | 'display';

interface ButtonMappingDialogProps {
  open: boolean;
  device: 'controller' | 'drivebase';
  onClose: () => void;
  onSave: (button: string, bindings: InputBinding[]) => boolean;
  initialButton?: string;
}

function actionToLabel(bindingAction: InputBindingAction): string {
  const actionName = String(bindingAction.action?.name || '').trim();
  if (!actionName) return '';
  const parts: string[] = [];
  if (bindingAction.parentName) parts.push(bindingAction.parentName);
  if (bindingAction.mechanismName) parts.push(bindingAction.mechanismName);
  parts.push(actionName);
  return buildActionLabel(parts);
}

function argsToValues(args: FrameworkArgument[] | undefined): Record<string, string> {
  const values: Record<string, string> = {};
  (args || []).forEach((arg) => {
    if (isSequenceTupleArg(arg)) {
      if (Array.isArray(arg.value)) {
        Object.assign(values, sequenceTupleRowsToArgValues(arg, arg.value));
      } else if (arg.value != null && isUnsupportedSequenceTupleLiteral(String(arg.value), arg.members ?? [])) {
        // Preserve an unparseable / wrong-shape expression so it renders as unsupported code, not lost.
        values[arg.name] = String(arg.value).trim();
      }
      return;
    }
    if (isTupleArg(arg)) {
      if (arg.rawSourceExpression) {
        values[arg.name] = arg.rawSourceExpression;
        setRawActionArgValue(values, arg.name, arg.rawSourceExpression);
      }
      (arg.members || []).forEach((member, idx) => {
        const stored = readTupleMemberValue(arg, idx, member.name);
        const resolved = stored !== undefined && stored !== null ? stored : tupleMemberSchemaDefault(arg, idx, member as FrameworkArgument);
        values[tupleMemberValueKey(arg.name, member.name)] = resolved === undefined || resolved === null ? '' : String(resolved);
      });
      return;
    }
    values[arg.name] = String(arg.value ?? '');
    if (arg.rawSourceExpression) setRawActionArgValue(values, arg.name, arg.rawSourceExpression);
  });
  return values;
}

function createEmptyPressedDraft(draftId: string): EditableBindingDraft {
  return { draftId, kind: INPUT_BINDING_KIND.PRESSED, actionLabel: '', argValues: {} };
}

function createEmptyToggleDraft(draftId: string): ToggleBindingDraft {
  return { draftId, kind: INPUT_BINDING_KIND.TOGGLE, onActionLabel: '', onArgValues: {}, offActionLabel: '', offArgValues: {} };
}

function normalizedDraftSnapshot(drafts: EditableBindingDraft[]): string {
  return JSON.stringify(
    drafts.map((draft) =>
      draft.kind === INPUT_BINDING_KIND.TOGGLE
        ? {
            kind: draft.kind,
            onActionLabel: draft.onActionLabel,
            onArgValues: Object.fromEntries(Object.entries(draft.onArgValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
            offActionLabel: draft.offActionLabel,
            offArgValues: Object.fromEntries(Object.entries(draft.offArgValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
          }
        : {
            kind: draft.kind,
            actionLabel: draft.actionLabel,
            argValues: Object.fromEntries(Object.entries(draft.argValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
          }
    )
  );
}

function canonicalBindingSnapshot(bindings: InputBinding[]): string {
  return JSON.stringify(bindings.map(inputBindingSignature).sort());
}

const ButtonMappingDialog: React.FC<ButtonMappingDialogProps> = ({ open, device, onClose, onSave, initialButton }) => {
  const { t } = useTranslation();
  const buttonLabelSource = useMemo(() => (device === 'drivebase' ? DRIVEBASE_BUTTON_LABEL : CONTROLLER_BUTTON_LABEL), [device]);
  const defaultButtonKeys = useMemo(() => Object.keys(buttonLabelSource), [buttonLabelSource]);
  const labelForButtonOption = useCallback(
    (opt: string) => (device === 'drivebase' ? translateDrivebaseInputButton(opt, t) : translateControllerInputButton(opt, t)),
    [device, t]
  );
  const [button, setButton] = useState<string>(() => initialButton || defaultButtonKeys[0] || '');
  const [drafts, setDrafts] = useState<EditableBindingDraft[]>([]);
  const [initialEffectiveSnapshot, setInitialEffectiveSnapshot] = useState<string>('[]');
  const [pendingButton, setPendingButton] = useState<string | null>(null);

  const allAvailableActions = useAllAvailableActions();
  const globalActionList = useMemo(() => excludeGroupOnlyActions(allAvailableActions), [allAvailableActions]);
  const globalAxisActionList = useAppSelector((state) => state.editor.axisActionList);
  const globalInputBindingList = useAppSelector((state) => state.editor.inputBindingList);
  const project = useAppSelector((state) => state.project.loadedProject);
  const inputBindingListRef = useRef<InputBinding[]>(globalInputBindingList);
  const draftIdCounterRef = useRef(0);
  const pathOptions = useMemo(
    () => ((project?.paths || []) as Array<{ id: string; name: string }>).map(({ id, name }) => ({ id, name })),
    [project?.paths]
  );
  const bindingKindLabels = useMemo<Record<EditableBindingKind, string>>(
    () => ({
      [INPUT_BINDING_KIND.PRESSED]: t('controller.buttonMapping.triggerPressed', 'Pressed'),
      [INPUT_BINDING_KIND.RELEASED]: t('controller.buttonMapping.triggerReleased', 'Released'),
      [INPUT_BINDING_KIND.VARIABLE]: t('controller.buttonMapping.triggerVariable', 'Variable'),
      [INPUT_BINDING_KIND.TOGGLE]: t('controller.buttonMapping.triggerToggle', 'Toggle'),
    }),
    [t]
  );
  const dialogTitle = t('controller.buttonMapping.dialog.title', {
    button: labelForButtonOption(button) || button,
    defaultValue: 'Edit {{button}} mappings',
  });

  useEffect(() => {
    inputBindingListRef.current = globalInputBindingList;
  }, [globalInputBindingList]);

  const canUseVariable = useCallback((btn: string) => canUseVariableInputBinding(device, btn), [device]);

  const nextDraftId = useCallback((): string => {
    draftIdCounterRef.current += 1;
    return `binding-draft-${draftIdCounterRef.current}`;
  }, []);

  const bindingToDraft = useCallback(
    (binding: InputBinding): EditableBindingDraft => {
      if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
        return {
          draftId: nextDraftId(),
          kind: INPUT_BINDING_KIND.TOGGLE,
          onActionLabel: actionToLabel(binding.onAction),
          onArgValues: argsToValues(binding.onAction.action?.arguments),
          offActionLabel: actionToLabel(binding.offAction),
          offArgValues: argsToValues(binding.offAction.action?.arguments),
        };
      }
      if (binding.kind === INPUT_BINDING_KIND.UNSUPPORTED) return createEmptyPressedDraft(nextDraftId());
      return {
        draftId: nextDraftId(),
        kind: binding.kind,
        actionLabel: actionToLabel(binding.action),
        argValues: argsToValues(binding.action.action?.arguments),
      };
    },
    [nextDraftId]
  );

  const getEditableBindingsForButton = useCallback(
    (btn: string): InputBinding[] => {
      return dedupeInputBindingsBySignature(getSupportedInputBindingsForButtonEditor(inputBindingListRef.current, device, btn));
    },
    [device]
  );

  const createDraftsForButton = useCallback(
    (btn: string): EditableBindingDraft[] => {
      const nextDrafts = getEditableBindingsForButton(btn).map(bindingToDraft);
      return nextDrafts.length > 0 ? nextDrafts : [createEmptyPressedDraft(nextDraftId())];
    },
    [bindingToDraft, getEditableBindingsForButton, nextDraftId]
  );

  const buttonOptions = useMemo(() => {
    const set = new Set<string>(defaultButtonKeys);
    if (button && !set.has(button)) set.add(button);
    if (initialButton && !set.has(initialButton)) set.add(initialButton);
    return Array.from(set);
  }, [button, defaultButtonKeys, initialButton]);

  const actionDefForLabel = useCallback(
    (kind: EditableBindingKind, actionLabel: string): FrameworkAction | null => {
      const actionDef = findActionDefForLabel(kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList || [] : allAvailableActions, actionLabel);
      if (!actionDef) return null;
      if (kind !== INPUT_BINDING_KIND.VARIABLE) return actionDef;
      const inputArgName = getInputArgNameForAxisAction(actionDef.name);
      return {
        ...actionDef,
        arguments: (actionDef.arguments || []).filter((arg) => arg.name !== inputArgName),
      };
    },
    [allAvailableActions, globalAxisActionList]
  );

  const actionItemForLabel = useCallback(
    (kind: EditableBindingKind, actionLabel: string) =>
      findActionItemForLabel(kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList || [] : allAvailableActions, actionLabel),
    [allAvailableActions, globalAxisActionList]
  );

  const actionDefForLabelAndValues = useCallback(
    (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>): FrameworkAction | null =>
      withResolvedMotorVelocityLimits(actionDefForLabel(kind, actionLabel), argValues),
    [actionDefForLabel]
  );

  const argValuesForAction = useCallback(
    (actionDef: FrameworkAction | null, mode: ArgHydrationMode): Record<string, string> => {
      if (!actionDef?.arguments || actionDef.arguments.length === 0) return {};
      const values: Record<string, string> = {};

      const seedScalar = (arg: FrameworkArgument, key: string) => {
        const defaultVal = arg.defaultValue ?? arg.value;
        if (defaultVal !== undefined && defaultVal !== null && String(defaultVal) !== '') {
          values[key] = String(defaultVal);
          return;
        }
        if (mode === 'semantic') return;
        const enumOpts = isFrameworkNumberArgType(arg.type || '') ? undefined : FRAMEWORK_ENUM_OPTIONS[arg.type || ''];
        if (isFollowPathAction(actionDef.name) && arg.name === 'path' && pathOptions.length > 0) {
          values[key] = pathOptions[0].name;
        } else if (enumOpts && enumOpts.length > 0) {
          values[key] = enumOpts[0].value;
        } else {
          values[key] = '';
        }
      };

      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // No default rows: a sequence-of-tuples starts empty and rows are user-created
          // (or hydrated from existing code). Nothing to seed here.
          return;
        }
        if (isTupleArg(frameworkArg)) {
          (frameworkArg.members || []).forEach((member, idx) => {
            const m = member as FrameworkArgument;
            const key = tupleMemberValueKey(frameworkArg.name, m.name);
            const fromSchema = tupleMemberSchemaDefault(frameworkArg, idx, m);
            if (fromSchema !== undefined && fromSchema !== null && String(fromSchema) !== '') {
              values[key] = String(fromSchema);
              return;
            }
            seedScalar(m, key);
          });
          return;
        }
        seedScalar(frameworkArg, frameworkArg.name);
      });
      return values;
    },
    [pathOptions]
  );

  const syncArgValuesForAction = useCallback(
    (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>, mode: ArgHydrationMode): Record<string, string> => {
      if (!actionLabel) return {};
      const actionDef = actionDefForLabel(kind, actionLabel);
      if (!actionDef?.arguments || actionDef.arguments.length === 0) {
        return {};
      }
      const defaults = argValuesForAction(actionDef, mode);
      const nextValues: Record<string, string> = {};

      const copyScalar = (arg: FrameworkArgument, key: string) => {
        const current = argValues[key];
        const rawSourceValue = getRawActionArgValue(argValues, key);
        if (
          mode === 'display' &&
          isFollowPathAction(actionDef.name) &&
          arg.name === 'path' &&
          current &&
          !rawSourceValue &&
          !pathOptions.some((path) => path.name === current)
        ) {
          nextValues[key] = '';
          return;
        }
        nextValues[key] = current ?? defaults[key] ?? '';
      };
      const copiedValueKeys: string[] = [];

      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // Carry the indexed row keys (`<arg>.<row>.<member>`) and any preserved
          // unsupported raw value (`<arg>`) through untouched.
          for (const [key, value] of Object.entries(argValues)) {
            if (key === frameworkArg.name || key.startsWith(`${frameworkArg.name}.`)) nextValues[key] = value;
          }
          return;
        }
        if (isTupleArg(frameworkArg)) {
          const tupleRawSourceValue = getRawActionArgValue(argValues, frameworkArg.name);
          if (tupleRawSourceValue !== undefined) {
            nextValues[frameworkArg.name] = tupleRawSourceValue;
            copiedValueKeys.push(frameworkArg.name);
          }
          (frameworkArg.members || []).forEach((member) => {
            const key = tupleMemberValueKey(frameworkArg.name, member.name);
            copiedValueKeys.push(key);
            copyScalar(member as FrameworkArgument, key);
          });
          return;
        }
        copiedValueKeys.push(frameworkArg.name);
        copyScalar(frameworkArg, frameworkArg.name);
      });
      copyRawActionArgValues(argValues, nextValues, copiedValueKeys);
      return nextValues;
    },
    [actionDefForLabel, argValuesForAction, pathOptions]
  );

  const syncArgValues = useCallback(
    (draft: EditableBindingDraft, mode: ArgHydrationMode = 'display'): EditableBindingDraft => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return {
          ...draft,
          onArgValues: syncArgValuesForAction(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel, draft.onArgValues, mode),
          offArgValues: syncArgValuesForAction(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel, draft.offArgValues, mode),
        };
      }
      return { ...draft, argValues: syncArgValuesForAction(draft.kind, draft.actionLabel, draft.argValues, mode) };
    },
    [syncArgValuesForAction]
  );

  useEffect(() => {
    if (!open) return;
    setDrafts((prev) => {
      const next = prev.map((draft) => syncArgValues(draft));
      return normalizedDraftSnapshot(prev) === normalizedDraftSnapshot(next) ? prev : next;
    });
  }, [open, syncArgValues]);

  const allowedKinds = useMemo<EditableBindingKind[]>(
    () =>
      canUseVariable(button)
        ? [INPUT_BINDING_KIND.PRESSED, INPUT_BINDING_KIND.RELEASED, INPUT_BINDING_KIND.VARIABLE, INPUT_BINDING_KIND.TOGGLE]
        : [INPUT_BINDING_KIND.PRESSED, INPUT_BINDING_KIND.RELEASED, INPUT_BINDING_KIND.TOGGLE],
    [button, canUseVariable]
  );

  const updateDraft = (draftId: string, updater: (draft: EditableBindingDraft) => EditableBindingDraft) => {
    setDrafts((prev) => prev.map((draft) => (draft.draftId === draftId ? updater(draft) : draft)));
  };

  const handleKindChange = (draftId: string, nextKind: EditableBindingKind) => {
    updateDraft(draftId, (draft) => {
      if (nextKind === INPUT_BINDING_KIND.TOGGLE) return createEmptyToggleDraft(draft.draftId);
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) return { draftId: draft.draftId, kind: nextKind, actionLabel: '', argValues: {} };
      const changedVariableBoundary = draft.kind === INPUT_BINDING_KIND.VARIABLE || nextKind === INPUT_BINDING_KIND.VARIABLE;
      return {
        ...draft,
        kind: nextKind,
        actionLabel: changedVariableBoundary ? '' : draft.actionLabel,
        argValues: changedVariableBoundary ? {} : draft.argValues,
      };
    });
  };

  // Seed a newly selected action with one editable row per sequence-of-tuples argument so the
  // user doesn't have to click "Add" to get started. Rows can still be added/removed afterward.
  const initialArgValuesForLabel = useCallback(
    (kind: EditableBindingKind, label: string): Record<string, string> => {
      const actionDef = actionDefForLabel(kind, label);
      const seeded: Record<string, string> = {};
      (actionDef?.arguments || []).forEach((arg) => {
        if (isSequenceTupleArg(arg as FrameworkArgument)) Object.assign(seeded, defaultSequenceTupleRowArgValues(arg as FrameworkArgument));
      });
      return seeded;
    },
    [actionDefForLabel]
  );

  const handleActionChange = (draftId: string, nextLabel: string, slot?: 'on' | 'off') => {
    updateDraft(draftId, (draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        const seeded = initialArgValuesForLabel(INPUT_BINDING_KIND.TOGGLE, nextLabel);
        const nextDraft =
          slot === 'off'
            ? { ...draft, offActionLabel: nextLabel, offArgValues: seeded }
            : { ...draft, onActionLabel: nextLabel, onArgValues: seeded };
        return syncArgValues(nextDraft);
      }
      return syncArgValues({ ...draft, actionLabel: nextLabel, argValues: initialArgValuesForLabel(draft.kind, nextLabel) });
    });
  };

  const handleAddDraft = () => {
    setDrafts((prev) => [...prev, createEmptyPressedDraft(nextDraftId())]);
  };

  const handleRemoveDraft = (draftId: string) => {
    setDrafts((prev) => prev.filter((draft) => draft.draftId !== draftId));
  };

  const updateArgValuesForAction = (actionDef: FrameworkAction | null, argValues: Record<string, string>, name: string, value: string) => {
    const next = updateRawActionArgValue(argValues, name, value);
    const tupleArg = (actionDef?.arguments || []).find((arg) =>
      isTupleArg(arg as FrameworkArgument)
        ? (arg as FrameworkArgument).members?.some((member) => tupleMemberValueKey(arg.name, member.name) === name)
        : false
    ) as FrameworkArgument | undefined;
    if (tupleArg) {
      delete next[tupleArg.name];
      clearRawActionArgValue(next, tupleArg.name);
    }
    return next;
  };

  const buildBindingAction = (
    actionLabel: string,
    actionDef: FrameworkAction | null,
    actionItem: ReturnType<typeof findActionItemForLabel>,
    argValues: Record<string, string>
  ): InputBindingAction => {
    const { parentName, mechanismName, actionName } = parseActionLabel(actionLabel);
    const args: FrameworkArgument[] =
      actionLabel && actionDef?.arguments
        ? actionDef.arguments.map((arg) => {
            const frameworkArg = arg as FrameworkArgument;
            if (isSequenceTupleArg(frameworkArg)) {
              const rows = sequenceTupleArgValueFromArgValues(frameworkArg, argValues, coerceFrameworkArgValuePreservingExpressions);
              const rawValue = String(argValues[frameworkArg.name] ?? '').trim();
              return {
                name: frameworkArg.name,
                type: frameworkArg.type,
                members: frameworkArg.members,
                // No rows but a preserved unsupported expression => keep it verbatim.
                value: rows.length === 0 && rawValue ? rawValue : rows,
              };
            }
            if (isTupleArg(frameworkArg)) {
              const rawSourceExpression = getRawActionArgValue(argValues, frameworkArg.name);
              return {
                name: frameworkArg.name,
                type: frameworkArg.type,
                members: frameworkArg.members,
                value: rawSourceExpression || tupleValueFromArgValues(frameworkArg, argValues, coerceFrameworkArgValuePreservingExpressions),
                rawSourceExpression,
              };
            }
            const raw = String(argValues[frameworkArg.name] ?? '');
            const value =
              raw === '' && isFrameworkArgOptionalNoneDefault(frameworkArg) ? null : coerceFrameworkArgValuePreservingExpressions(frameworkArg, raw);
            return {
              name: frameworkArg.name,
              type: frameworkArg.type,
              value,
              rawSourceExpression: getRawActionArgValue(argValues, frameworkArg.name),
            };
          })
        : [];
    return {
      parentName,
      parentClass: actionItem?.parentClass,
      mechanismName,
      mechanismClass: actionItem?.mechanismClass,
      action: { name: actionLabel ? actionName : '', arguments: args },
    };
  };

  const buildBindingsForDrafts = useCallback(
    (targetButton: string, targetDrafts: EditableBindingDraft[]): InputBinding[] => {
      const nextBindings = targetDrafts
        .map((draft): InputBinding | null => {
          if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
            if (!String(draft.onActionLabel || '').trim() || !String(draft.offActionLabel || '').trim()) return null;
            return withInputBindingId({
              device,
              button: targetButton,
              kind: INPUT_BINDING_KIND.TOGGLE,
              onAction: buildBindingAction(
                draft.onActionLabel,
                actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel),
                actionItemForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel),
                draft.onArgValues
              ),
              offAction: buildBindingAction(
                draft.offActionLabel,
                actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel),
                actionItemForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel),
                draft.offArgValues
              ),
            });
          }
          if (!String(draft.actionLabel || '').trim()) return null;
          return withInputBindingId({
            device,
            button: targetButton,
            kind: draft.kind,
            action: buildBindingAction(
              draft.actionLabel,
              actionDefForLabel(draft.kind, draft.actionLabel),
              actionItemForLabel(draft.kind, draft.actionLabel),
              draft.argValues
            ),
          });
        })
        .filter((binding): binding is InputBinding => binding !== null);
      return dedupeInputBindingsBySignature(nextBindings);
    },
    [actionDefForLabel, actionItemForLabel, device]
  );

  const buildBindingsFromDrafts = useCallback((): InputBinding[] => {
    return buildBindingsForDrafts(button, drafts);
  }, [buildBindingsForDrafts, button, drafts]);

  const loadButtonDrafts = useCallback(
    (nextButton: string) => {
      const parsedDrafts = createDraftsForButton(nextButton);
      const semanticDrafts = parsedDrafts.map((draft) => syncArgValues(draft, 'semantic'));
      const nextDrafts = parsedDrafts.map((draft) => syncArgValues(draft, 'display'));
      setButton(nextButton);
      setDrafts(nextDrafts);
      setInitialEffectiveSnapshot(canonicalBindingSnapshot(buildBindingsForDrafts(nextButton, semanticDrafts)));
    },
    [buildBindingsForDrafts, createDraftsForButton, syncArgValues]
  );

  useLayoutEffect(() => {
    if (!open) return;
    loadButtonDrafts(initialButton || defaultButtonKeys[0] || '');
    setPendingButton(null);
  }, [open, initialButton, defaultButtonKeys, loadButtonDrafts]);

  const canonicalDraftBindings = useMemo(() => buildBindingsFromDrafts(), [buildBindingsFromDrafts]);
  const currentEffectiveSnapshot = useMemo(() => canonicalBindingSnapshot(canonicalDraftBindings), [canonicalDraftBindings]);
  const isDirty = currentEffectiveSnapshot !== initialEffectiveSnapshot;

  const canSave = useMemo(() => {
    if (!button) return false;
    const scalarArgIsValid = (arg: FrameworkArgument, valueKey: string, argValues: Record<string, string>) =>
      scalarFrameworkArgIsValid(arg, valueKey, argValues, t);
    const actionIsValid = (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>, required: boolean) => {
      if (!actionLabel) return !required;
      const actionDef = actionDefForLabelAndValues(kind, actionLabel, argValues);
      const args = actionDef?.arguments || [];
      return args.every((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // Each row's members must be valid (e.g. line_number in range) and the bounded-integer
          // key member (e.g. line_number) must be unique across rows.
          return sequenceTupleRowsAreValid(frameworkArg, argValues, t);
        }
        if (isTupleArg(frameworkArg)) {
          if (getRawActionArgValue(argValues, frameworkArg.name)) return true;
          return (frameworkArg.members || []).every((member) =>
            scalarArgIsValid(member as FrameworkArgument, tupleMemberValueKey(frameworkArg.name, member.name), argValues)
          );
        }
        return scalarArgIsValid(frameworkArg, frameworkArg.name, argValues);
      });
    };
    return drafts.every((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        const hasAnyToggleAction = !!draft.onActionLabel || !!draft.offActionLabel;
        return (
          actionIsValid(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel, draft.onArgValues, hasAnyToggleAction) &&
          actionIsValid(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel, draft.offArgValues, hasAnyToggleAction)
        );
      }
      return actionIsValid(draft.kind, draft.actionLabel, draft.argValues, false);
    });
  }, [actionDefForLabelAndValues, button, drafts, t]);

  const saveDrafts = (): boolean => {
    if (!canSave) return false;
    const didSave = onSave(button, canonicalDraftBindings);
    if (didSave) {
      setInitialEffectiveSnapshot(currentEffectiveSnapshot);
    }
    return didSave;
  };

  const handleUpdate = () => {
    if (saveDrafts()) onClose();
  };

  const requestButtonChange = (event: SelectChangeEvent<string>) => {
    const nextButton = event.target.value;
    if (nextButton === button) return;
    if (isDirty) {
      setPendingButton(nextButton);
      return;
    }
    loadButtonDrafts(nextButton);
  };

  const discardAndSwitchButton = () => {
    if (!pendingButton) return;
    loadButtonDrafts(pendingButton);
    setPendingButton(null);
  };

  const updateAndSwitchButton = () => {
    if (!pendingButton) return;
    if (!saveDrafts()) return;
    loadButtonDrafts(pendingButton);
    setPendingButton(null);
  };

  const argumentLabelWidthCh = useMemo(() => {
    const allLabels = drafts.flatMap((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return [
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkArgLabel(t, arg.name)
          ) || []),
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkArgLabel(t, arg.name)
          ) || []),
        ];
      }
      return actionDefForLabel(draft.kind, draft.actionLabel)?.arguments?.map((arg) => getLocalizedFrameworkArgLabel(t, arg.name)) || [];
    });
    const longest = allLabels.reduce((max, label) => Math.max(max, label.length), 0);
    return Math.min(Math.max(longest + 1, 8), 18);
  }, [actionDefForLabel, drafts, t]);

  const argumentUnitWidthCh = useMemo(() => {
    const allUnits = drafts.flatMap((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return [
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
          ) || []),
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
          ) || []),
        ];
      }
      return (
        actionDefForLabel(draft.kind, draft.actionLabel)?.arguments?.map((arg) =>
          getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
        ) || []
      );
    });
    const longest = allUnits.reduce((max, unit) => Math.max(max, unit.length), 0);
    if (longest === 0) return 0;
    return Math.min(Math.max(longest + 1, 6), 16);
  }, [actionDefForLabel, drafts, t]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { maxHeight: '82vh' } }}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1 }}>
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('controller.buttonMapping.dialog.buttonLabel', 'Button')}
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  size="small"
                  value={button}
                  onChange={requestButtonChange}
                  sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                >
                  {buttonOptions.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: 12 }}>
                      {labelForButtonOption(opt) || opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: '52vh' }}>
              <Stack spacing={1.25}>
                {drafts.map((draft) => {
                  const actionDef =
                    draft.kind === INPUT_BINDING_KIND.TOGGLE ? null : actionDefForLabelAndValues(draft.kind, draft.actionLabel, draft.argValues);
                  const actionArgs = actionDef?.arguments || [];
                  return (
                    <Box
                      key={draft.draftId}
                      data-testid="binding-draft"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Box sx={{ flex: '0 0 128px' }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                size="small"
                                value={draft.kind}
                                onChange={(event) => handleKindChange(draft.draftId, event.target.value as EditableBindingKind)}
                                inputProps={{ 'aria-label': t('controller.buttonMapping.dialog.mappingTypeAriaLabel', 'Mapping type') }}
                                sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                              >
                                {allowedKinds.map((kind) => (
                                  <MenuItem key={kind} value={kind} sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                                    {bindingKindLabels[kind]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          {draft.kind !== INPUT_BINDING_KIND.TOGGLE && (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <ActionSelector
                                value={draft.actionLabel}
                                onChange={(value) => handleActionChange(draft.draftId, value)}
                                actions={draft.kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList : globalActionList}
                                customActions={draft.kind === INPUT_BINDING_KIND.VARIABLE ? [] : undefined}
                                allowNone
                                noneLabel={t('controller.buttonMapping.dialog.selectAction', 'Select action')}
                                ariaLabel={t('controller.buttonMapping.dialog.mappingActionAriaLabel', 'Mapping action')}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <IconButton
                              size="small"
                              aria-label={t('controller.buttonMapping.dialog.removeMappingAriaLabel', 'Remove mapping')}
                              onClick={() => handleRemoveDraft(draft.draftId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Stack>

                        {draft.kind === INPUT_BINDING_KIND.TOGGLE && (
                          <Stack spacing={1.25}>
                            {(['on', 'off'] as const).map((slot) => {
                              const label = slot === 'on' ? draft.onActionLabel : draft.offActionLabel;
                              const argValues = slot === 'on' ? draft.onArgValues : draft.offArgValues;
                              const slotActionDef = actionDefForLabelAndValues(INPUT_BINDING_KIND.TOGGLE, label, argValues);
                              const slotArgs = slotActionDef?.arguments || [];
                              return (
                                <Box key={slot}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {slot === 'on'
                                      ? t('controller.buttonMapping.dialog.toggleOnLabel', 'Toggle on')
                                      : t('controller.buttonMapping.dialog.toggleOffLabel', 'Toggle off')}
                                  </Typography>
                                  <ActionSelector
                                    value={label}
                                    onChange={(value) => handleActionChange(draft.draftId, value, slot)}
                                    actions={globalActionList}
                                    allowNone
                                    noneLabel={
                                      slot === 'on'
                                        ? t('controller.buttonMapping.dialog.selectToggleOnAction', 'Select toggle on action')
                                        : t('controller.buttonMapping.dialog.selectToggleOffAction', 'Select toggle off action')
                                    }
                                    ariaLabel={
                                      slot === 'on'
                                        ? t('controller.buttonMapping.dialog.toggleOnActionAriaLabel', 'Toggle on action')
                                        : t('controller.buttonMapping.dialog.toggleOffActionAriaLabel', 'Toggle off action')
                                    }
                                  />
                                  {slotArgs.length > 0 && (
                                    <Box sx={{ mt: 1.25 }}>
                                      <FrameworkActionArgumentFields
                                        actionDef={slotActionDef}
                                        values={argValues}
                                        onArgChange={(name, value) =>
                                          updateDraft(draft.draftId, (current) => {
                                            if (current.kind !== INPUT_BINDING_KIND.TOGGLE) return current;
                                            return slot === 'on'
                                              ? { ...current, onArgValues: updateArgValuesForAction(slotActionDef, current.onArgValues, name, value) }
                                              : {
                                                  ...current,
                                                  offArgValues: updateArgValuesForAction(slotActionDef, current.offArgValues, name, value),
                                                };
                                          })
                                        }
                                        onArgValuesChange={(next) =>
                                          updateDraft(draft.draftId, (current) => {
                                            if (current.kind !== INPUT_BINDING_KIND.TOGGLE) return current;
                                            return slot === 'on' ? { ...current, onArgValues: next } : { ...current, offArgValues: next };
                                          })
                                        }
                                        pathOptions={pathOptions}
                                        enumOptions={FRAMEWORK_ENUM_OPTIONS}
                                        t={t}
                                        layout="row"
                                        showUnits
                                        labelWidthCh={argumentLabelWidthCh}
                                        unitWidthCh={argumentUnitWidthCh}
                                        spacing={1.5}
                                        idPrefix={`${draft.draftId}-${slot}`}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}

                        {draft.kind !== INPUT_BINDING_KIND.TOGGLE && actionArgs.length > 0 && (
                          <Box>
                            <FrameworkActionArgumentFields
                              actionDef={actionDef}
                              values={draft.argValues}
                              onArgChange={(name, value) =>
                                updateDraft(draft.draftId, (current) =>
                                  current.kind === INPUT_BINDING_KIND.TOGGLE
                                    ? current
                                    : {
                                        ...current,
                                        argValues: updateArgValuesForAction(actionDef, current.argValues, name, value),
                                      }
                                )
                              }
                              onArgValuesChange={(next) =>
                                updateDraft(draft.draftId, (current) =>
                                  current.kind === INPUT_BINDING_KIND.TOGGLE ? current : { ...current, argValues: next }
                                )
                              }
                              pathOptions={pathOptions}
                              enumOptions={FRAMEWORK_ENUM_OPTIONS}
                              t={t}
                              layout="row"
                              showUnits
                              labelWidthCh={argumentLabelWidthCh}
                              unitWidthCh={argumentUnitWidthCh}
                              spacing={1.5}
                              idPrefix={draft.draftId}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Button startIcon={<AddIcon />} onClick={handleAddDraft} size="small">
                {t('controller.buttonMapping.dialog.addMapping', 'Add mapping')}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('controller.buttonMapping.dialog.cancel', 'Cancel')}</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={!canSave || !isDirty}>
            {t('controller.buttonMapping.dialog.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingButton !== null} onClose={() => setPendingButton(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('controller.buttonMapping.dialog.unsavedTitle', 'Unsaved mapping changes')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('controller.buttonMapping.dialog.unsavedBody', 'Update this button before switching, or discard the current draft changes.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingButton(null)}>{t('controller.buttonMapping.dialog.cancel', 'Cancel')}</Button>
          <Button onClick={discardAndSwitchButton}>{t('controller.buttonMapping.dialog.discardChanges', 'Discard changes')}</Button>
          <Button onClick={updateAndSwitchButton} variant="contained" disabled={!canSave}>
            {t('controller.buttonMapping.dialog.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ButtonMappingDialog;
